import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { NegotiatorTransaction } from '../types/transaction';
import { buildRetrocessionRowAmounts } from '../utils/retrocessions';

interface NegotiatorRow {
  negotiator_id: string;
  role: string;
  percentage: number;
  agency: string | null;
}

interface RetrocessionRow {
  type: string;
  amount: number;
  distribution_type: string;
  distribution_percentages: Record<string, number> | null;
  primary_negotiator_id: string | null;
  secondary_negotiator_id: string | null;
  tertiary_negotiator_id: string | null;
  quaternary_negotiator_id: string | null;
}

export function useNegotiatorTransactions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadTransactions = async (
    negotiatorId: string,
    startDate?: string,
    endDate?: string,
    agencyName?: string
  ) => {
    try {
      setLoading(true);
      
      console.log('Loading transactions for negotiator:', negotiatorId);
      console.log('Date range:', { startDate, endDate });
      console.log('Agency filter:', agencyName);

      let query = supabase
        .from('transactions')
        .select(`
          id,
          property_number,
          seller,
          buyer,
          sale_price,
          fees_with_tax,
          fees_without_tax,
          status,
          compromise_date,
          transaction_agencies!inner (
            agency
          ),
          transaction_retrocessions!left (
            id,
            type,
            amount,
            description,
            distribution_type,
            distribution_percentages,
            primary_negotiator_id,
            secondary_negotiator_id,
            tertiary_negotiator_id,
            quaternary_negotiator_id
          ),
          transaction_negotiators!inner (
            negotiator_id,
            role,
            percentage,
            agency
          )
        `)
        .eq('transaction_negotiators.negotiator_id', negotiatorId);

      // Si des dates sont fournies, filtrer les transactions non potentielles par date
      if (startDate && endDate) {
        query = query.or(
          `status.eq.potentiel,status.eq.annule,and(status.in.(compromis,acte,encaisse,cloture),compromise_date.gte.${startDate},compromise_date.lte.${endDate})`
        );
      }

      const { data, error: transactionsError } = await query;

      if (transactionsError) throw transactionsError;

      console.log('Raw transactions data:', data);

      let filteredData = data;

      // Filtrer par agence si fourni (pour la vue "Par agence")
      // On réplique la logique de résolution de useAgencyStats : priorité à
      // transaction_negotiators.agency, sinon déduit depuis les associations
      // negotiator_agencies actives à la date du compromis (ou fin de période).
      if (agencyName) {
        const { data: negotiatorAgencies, error: agenciesError } = await supabase
          .from('negotiator_agencies')
          .select('agency, start_date, end_date')
          .eq('negotiator_id', negotiatorId);

        if (agenciesError) throw agenciesError;

        const periodEnd = endDate ? new Date(endDate) : new Date();

        const resolveAgency = (tn: NegotiatorRow, t: { compromise_date: string | null }) => {
          if (tn?.agency) return tn.agency;
          const txDate = t.compromise_date ? new Date(t.compromise_date) : periodEnd;
          const activeAssoc = negotiatorAgencies?.find(na => {
            const s = na.start_date ? new Date(na.start_date) : null;
            const e = na.end_date ? new Date(na.end_date) : null;
            return (!s || s <= txDate) && (!e || e >= txDate);
          });
          return activeAssoc?.agency || null;
        };

        // On filtre les LIGNES et non les transactions : un négociateur rattaché à
        // deux agences sur la même transaction n'y apparaît que pour l'agence vue
        filteredData = data
          .map(t => ({
            ...t,
            transaction_negotiators: t.transaction_negotiators.filter(
              (tn: NegotiatorRow) => resolveAgency(tn, t) === agencyName
            )
          }))
          .filter(t => t.transaction_negotiators.length > 0);
      }

      // Une entrée par ligne transaction_negotiators : un négociateur rattaché à
      // deux agences sur la même transaction doit apparaître deux fois (ex. 30% et 20%)
      const formattedTransactions: NegotiatorTransaction[] = filteredData.flatMap(t => {
        const rows: NegotiatorRow[] = t.transaction_negotiators.filter(
          (n: NegotiatorRow) => n.negotiator_id === negotiatorId
        );
        if (rows.length === 0) return [];

        // Rétrocessions réparties entre les lignes du négociateur : le montant
        // total qui lui revient reste inchangé, mais il n'est plus déduit deux fois
        const retrocessionsPerRow = buildRetrocessionRowAmounts(t.transaction_retrocessions, rows);

        return rows.map((negotiator, rowIndex) => {
          const baseRevenue = (t.fees_without_tax * negotiator.percentage) / 100;
          const rowRetrocession = retrocessionsPerRow[rowIndex] || 0;

          // Détail par type, au prorata de la part de cette ligne
          const totalRetrocessionAmount = (t.transaction_retrocessions || [])
            .reduce((sum: number, r: RetrocessionRow) => sum + r.amount, 0);
          const retrocessions = (t.transaction_retrocessions || []).map((r: RetrocessionRow) => ({
            type: r.type,
            amount: totalRetrocessionAmount > 0
              ? (rowRetrocession * r.amount) / totalRetrocessionAmount
              : 0
          }));

          return {
            id: t.id,
            property_number: t.property_number,
            seller: t.seller,
            buyer: t.buyer,
            sale_price: t.sale_price,
            fees_with_tax: t.fees_with_tax,
            fees_without_tax: t.fees_without_tax,
            status: t.status,
            agency: negotiator.agency || undefined,
            role: negotiator.role,
            percentage: negotiator.percentage,
            base_revenue: baseRevenue,
            retrocessions,
            final_revenue: baseRevenue - rowRetrocession,
            compromise_date: t.compromise_date
          };
        });
      });

      console.log('Final formatted transactions:', formattedTransactions);

      return formattedTransactions;
    } catch (err: any) {
      console.error('Error in loadTransactions:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  return { loadTransactions, loading, error };
}